import React from 'react'
import styled from 'styled-components'
import {ScrollArea, themeGet, Box, BoxProps, Text} from '../..'
import {ScrollableListProps} from './types'
// List
// --------------------------------------------
const ScrollableList = ({children, spacing = 'none', height = 408, ...props}: ScrollableListProps) => {
  return (
    <StyledScrollArea height={height} tabIndex={0}>
      <StyledScrollAreaViewport spacing={spacing}>{children}</StyledScrollAreaViewport>
      <StyledScrollAreaScrollbar orientation="vertical">
        <StyledScrollAreaThumb />
      </StyledScrollAreaScrollbar>
      <StyledScrollAreaScrollbar orientation="horizontal">
        <StyledScrollAreaThumb />
      </StyledScrollAreaScrollbar>
      <StyledScrollArea.Corner />
    </StyledScrollArea>
  )
}

// Item
// --------------------------------------------
interface ScrollableListItemProps extends Omit<BoxProps, 'event' | 'onSelect'> {
  /**
   * A visual that renders inside the Rule Item before the title and description
   */
  leadingVisual?: React.ReactNode | string | React.ComponentType<React.PropsWithChildren<{className?: string}>>
  /**
   * Content to render in the combobox item
   */
  children?: React.ReactNode
  /**
   * List item description
   */
  description?: string | React.ReactNode
  /**
   * List item label
   */
  label?: string | React.ReactNode
  /**
   * Whether items in the scrollable list should appear as Rule Items on individual cards separated by a marging, or as list items in a continuous list
   */
  appearance?: 'rule-item' | 'list-item'
  /**
   * Event handler called when the user selects an item (via mouse or keyboard). .
   */
  onSelect?: (event: Event) => void
  /**
   * Only to be used as a last resort
   */
  UNSAFE_className?: string
}
const Item = ({
  UNSAFE_className,
  leadingVisual: LeadingVisual,
  children,
  label,
  description,
  appearance,
  onSelect,
  ...props
}: ScrollableListItemProps) => {
  return (
    <ItemWrapper appearance={appearance} className={UNSAFE_className} {...props}>
      <InnerVisualSlot>{typeof LeadingVisual === 'function' ? <LeadingVisual /> : LeadingVisual}</InnerVisualSlot>
      {children}
      {label || description ? (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
          {label && <Text sx={{fontWeight: 'medium'}}>{label}</Text>}
          {description && <Text sx={{color: 'text.subtle'}}>{description}</Text>}
        </Box>
      ) : null}
    </ItemWrapper>
  )
}
// InnerVisualSlot
interface InnerVisualSlotProps {
  children?: React.ReactNode
}
/** Inner visual slot to house leading visual, trailing visual, counter, and loading spinners */
export const InnerVisualSlot = ({children}: InnerVisualSlotProps) => {
  // just render a visual
  return (
    <span className="scrollable-list-item-visual">
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>{children}</Box>
    </span>
  )
}

export const StyledScrollArea = styled(ScrollArea)<ScrollableListProps>`
  height: ${p => (p.height ? `${p.height}px` : '408px')};
  overflow: hidden;
  /* box-shadow: ${themeGet('shadows.elevation.shadow.overlay')}; */
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  --scrollbar-size: 10px;
`
export const StyledScrollAreaViewport = styled(ScrollArea.Viewport)<ScrollableListProps>`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  padding-right: ${p =>
    p.spacing === 'default'
      ? themeGet('space.3')
      : p.spacing === 'compact'
      ? themeGet('space.1')
      : p.spacing === 'none'
      ? 0
      : p.spacing === 'relaxed'
      ? themeGet('space.4')
      : themeGet('space.2')};
  padding-left: ${p =>
    p.spacing === 'default'
      ? themeGet('space.3')
      : p.spacing === 'compact'
      ? themeGet('space.1')
      : p.spacing === 'none'
      ? 0
      : p.spacing === 'relaxed'
      ? themeGet('space.4')
      : themeGet('space.2')};
  padding-top: ${p =>
    p.spacing === 'default'
      ? themeGet('space.4')
      : p.spacing === 'compact'
      ? themeGet('space.2')
      : p.spacing === 'none'
      ? 0
      : p.spacing === 'relaxed'
      ? themeGet('space.5')
      : themeGet('space.3')};
  padding-bottom: ${themeGet('space.4')};
`
export const StyledScrollAreaScrollbar = styled(ScrollArea.Scrollbar)`
  display: flex;
  /* ensures no selection */
  user-select: none;
  /* disable browser handling of all panning and zooming gestures on touch devices */
  touch-action: none;
  padding: 2px;
  background-color: ${themeGet('colors.background.neutral.subtle.default')};
  transition: background-color 160ms ease-out;
  &:hover {
    background-color: ${themeGet('colors.background.neutral.subtle.hovered')};
  }
  &[data-orientation='vertical'] {
    width: var(--scrollbar-size);
  }
  &[data-orientation='horizontal'] {
    flex-direction: column;
    height: var(--scrollbar-size);
  }
`
export const StyledScrollAreaThumb = styled(ScrollArea.Thumb)`
  flex: 1;
  background-color: ${themeGet('colors.background.neutral.default.pressed')};
  border-radius: var(--scrollbar-size);
  position: relative;
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 100%;
    min-width: 44px;
    min-height: 44px;
  }
`
export const ItemWrapper = styled(Box)<ScrollableListItemProps & BoxProps>`
  padding: ${themeGet('space.2')} 0;
  border-radius: ${p => (p.appearance === 'rule-item' ? themeGet('radii.2') : '')};
  outline: none;
  cursor: default;
  color: ${themeGet('colors.text.default')};
  font-size: ${themeGet('fontSizes.3')};
  position: relative;
  width: 100%;
  padding: 6px 12px 6px 16px;
  display: flex;
  align-items: center;
  gap: ${themeGet('space.2')};
  &:focus {
    background: ${themeGet('colors.background.neutral.subtle.hovered')};
    color: ${themeGet('colors.text.default')};
  }
  &:hover {
    background: ${themeGet('colors.background.neutral.subtle.hovered')};
    color: ${themeGet('colors.text.default')};
  }
  &[data-focus-visible] {
    background: ${themeGet('colors.background.neutral.subtle.hovered')};
    color: ${themeGet('colors.text.default')};
  }
  &[aria-selected='true'] {
    background-color: ${themeGet('colors.background.selected.default.hovered')};
    color: ${themeGet('colors.text.selected')};
    font-weight: 600;
    //
    box-shadow: inset 2px 0px 0px ${themeGet('colors.border.selected')};
    &:hover {
      background: ${themeGet('colors.background.neutral.subtle.hovered')};
      color: ${themeGet('colors.text.default')};
    }
  }
  &[data-pressed] {
    background-color: ${themeGet('colors.background.neutral.subtle.pressed')};
    color: ${themeGet('colors.text.default')};
  }
  &[aria-disabled] {
    background-color: ${themeGet('colors.elevation.surface.default.[default]')};
    color: ${themeGet('colors.text.disabled')};
    cursor: not-allowed;
  }

  @media (forced-colors: active) {
    &:focus {
      background: Highlight;
      color: HighlightText;
    }
  }
`
// Exports
Item.displayName = 'Item'
ScrollableList.displayName = 'ScrollableList'
export default Object.assign(ScrollableList, {Item})
